import React, { FC, useContext, useMemo } from 'react'

import productionsDetailsContext from '@/context/production-details'
import { CatalogAssetRelatedResource, CatalogImageAsset } from '@/types/app-types'
import { buildExternalImageUrl } from '@/utils/build-external-image-url'

import styles from './styles.module.scss'

const findExternalImagePath = (images: CatalogImageAsset[]) =>
    images.find((image) => image.dataModelName === 'EXTERNAL')?.path

const findResourcesByType = (relatedResources: CatalogAssetRelatedResource[], type: 'SUBCATEGORY' | 'CATEGORY') =>
    relatedResources.find((relatedResource) => relatedResource.type === type)

const buildPerformerImageUrl = (imagePrefix: string, externalImagePath: string) =>
    buildExternalImageUrl(imagePrefix, externalImagePath, 128, 128)

interface DesktopPerformerImageProps {
    alt: string | undefined
}

const DesktopPerformerImage: FC<DesktopPerformerImageProps> = ({ alt }) => {
    const {
        selectors: { productionDetails },
    } = useContext(productionsDetailsContext)

    const imagePrefix = process.env.NEXT_PUBLIC_EXTERNAL_IMAGE_URL_PREFIX

    const image = useMemo(() => {
        if (!productionDetails || !imagePrefix) return null

        const { assets } = productionDetails

        if (!assets) return null

        const defaultAlt = 'performer-image'

        const masterPerformerId = productionDetails.performers?.find(({ master }) => master)?.id
        const masterAssets = assets.find(({ id }) => id === masterPerformerId) || assets[0]
        const images = masterAssets.images ?? []
        const externalImagePath = findExternalImagePath(images)

        if (externalImagePath) {
            const src = buildPerformerImageUrl(imagePrefix, externalImagePath)
            return (
                <img
                    alt={alt || defaultAlt}
                    className={styles.performerImage}
                    data-testid="performer-image"
                    src={src}
                />
            )
        }

        const relatedResources = (assets && assets[0].relatedResources) ?? []

        const subCategoryResources = findResourcesByType(relatedResources, 'SUBCATEGORY')
        const subCategoryImages = (subCategoryResources && subCategoryResources.images) ?? []
        const subCategoryExternalImagePath = findExternalImagePath(subCategoryImages)

        if (subCategoryExternalImagePath) {
            const src = buildPerformerImageUrl(imagePrefix, subCategoryExternalImagePath)
            return (
                <img
                    alt={defaultAlt}
                    className={styles.performerImage}
                    data-testid="subcategory-performer-image"
                    src={src}
                />
            )
        }

        const categoryResources = findResourcesByType(relatedResources, 'CATEGORY')
        const categoryImages = (categoryResources && categoryResources.images) ?? []
        const categoryExternalImagePath = findExternalImagePath(categoryImages)

        if (categoryExternalImagePath) {
            const src = buildPerformerImageUrl(imagePrefix, categoryExternalImagePath)
            return (
                <img
                    alt={defaultAlt}
                    className={styles.performerImage}
                    data-testid="category-performer-image"
                    src={src}
                />
            )
        }

        return null
    }, [alt, imagePrefix, productionDetails])

    return image
}

export default DesktopPerformerImage
